import {Async, Loaded, isLoaded} from 'Core/models/Async';
import {DEFAULT_INITIAL_PLAN_NAME} from 'Quoting/constants';
import {RetreivedPlan} from 'Quoting/models/DTOs/QuoteForm';
import {A360ProductModel} from 'Quoting/models/Quoting';
import {parsePath} from 'Quoting/utils/utilities';
import {List as ImmutableList, Map as ImmutableMap, fromJS} from 'immutable';
import {createNewGVA6PlanObjectWithDefault} from './ACC/GVA6';
import {createNewGCI5PlanObjectWithDefault} from './CI/GCI5';
import {createNewGVCI4PlanObjectWithDefault} from './CI/GVCI4';
import {createNewPLPlan1ObjectWithDefault} from './GWL/Plan1';
import {getHospitalIndemnityPlanObject} from './HI/HIPlanHelper';

export const addNewPlanToQuote = (
  quote: Async<ImmutableMap<string, any>>,
  defaultValues: ImmutableMap<string, any> | undefined,
  plansWithCoverages: Async<RetreivedPlan[]>,
  planCd: string
) => {
  if (isLoaded(quote)) {
    let updatedPlans =
      (quote.Data?.getIn(parsePath('benefitPackagingDetail.plans')) as ImmutableList<any>) ?? ImmutableList();

    const plan =
      isLoaded(plansWithCoverages) && Array.isArray(plansWithCoverages.Data)
        ? plansWithCoverages.Data?.find(p => p.planCode === planCd)
        : undefined;

    const newPlanToAdd = createNewPlanObjectForSelectedProductPlan(quote.Data, plan, defaultValues);
    const quoteModel = quote?.Data?.get('_modelName') as A360ProductModel;

    updatedPlans = updatedPlans.push(fromJS({...newPlanToAdd, planName: DEFAULT_INITIAL_PLAN_NAME}));
    const quoteWithPlans = quote?.Data.withMutations(quoteData => {
      quoteData.setIn(
        parsePath('benefitPackagingDetail.packageCd'),
        updatePackageCd(
          plan?.planCode,
          quoteModel,
          quoteData.getIn(parsePath('benefitPackagingDetail.packageCd')) as string
        )
      );

      if (plan?.planCode === 'GCI5' || 'GVCI4') {
        quoteData.setIn(parsePath('commissionStyle'), defaultValues?.get('CIMasterPolicySummary.commissionStyle'));
        quoteData.setIn(
          parsePath('commonProvisionDetails.existERISAPlan'),
          defaultValues?.get('CICommonProvision.existERISAPlan')
        );
        quoteData.setIn(parsePath('rateStructureCd'), plan?.planCode !== 'GCI5' 
          ? defaultValues?.get('CIMasterPolicySummary.rateStructureCd') : undefined);
      }
      quoteData.setIn(parsePath('benefitPackagingDetail.plans'), updatedPlans);
    });
    return new Loaded(quoteWithPlans);
  }
  return quote;
};

const updatePackageCd = (planCd: string, model: A360ProductModel, packageCd: string) => {
  if (model == A360ProductModel.HIMaster) {
    return planCd === 'GHI3' ? 'HIPackageGHI3' : 'HIPackage';
  } else if (model == A360ProductModel.ACCMaster) {
    return planCd === 'GVA6' ? 'ACCPackageGVA6' : 'ACCPackage';
  } else if (model == A360ProductModel.CIMaster) {
    return planCd === 'GVCI4' ? 'CIPackageGVCI4' : 'CIPackage';
  } else {
    return packageCd;
  }
};

const createNewPlanObjectForSelectedProductPlan = (
  quoteData: ImmutableMap<string, any>,
  plan: RetreivedPlan | undefined,
  defaultValues: ImmutableMap<string, any> | undefined
) => {
  const coverageEffectiveDate = quoteData?.getIn(parsePath('transactionDetails.txEffectiveDate')) as string;
  const productModel = quoteData?.get('_modelName');
  const situsStateCd = quoteData?.get('riskStateCd');
  const quoteRootId = quoteData?.getIn(parsePath('_key.rootId')) as string;
  const quoteParentId = quoteData?.getIn(parsePath('_key.parentId')) as string;

  switch (productModel) {
    case A360ProductModel.HIMaster:
      return getHospitalIndemnityPlanObject(plan, quoteRootId, quoteParentId, coverageEffectiveDate, defaultValues);
    case A360ProductModel.CIMaster: {
      return getCriticalIllnessPlanObject(
        plan,
        quoteRootId,
        quoteParentId,
        coverageEffectiveDate,
        defaultValues,
        situsStateCd
      );
    }
    case A360ProductModel.ACCMaster: {
      return getAccidentPlanObject(
        plan,
        quoteRootId,
        quoteParentId,
        coverageEffectiveDate,
        defaultValues,
        situsStateCd
      );
    }
    case A360ProductModel.GPLUMaster: {
      return getLifePlanObject(plan, quoteRootId, quoteParentId, coverageEffectiveDate, defaultValues, situsStateCd);
    }
  }
};

const getLifePlanObject = (
  plan: RetreivedPlan | undefined,
  quoteRootId: string | undefined,
  quoteParentId: string | undefined,
  _coverageEffDate: string,
  defaultValues: ImmutableMap<string, any> | undefined,
  _situsStateCd: string
) => {
  switch (plan?.planCode) {
    case 'Plan1':
      return createNewPLPlan1ObjectWithDefault(
        plan,
        quoteRootId ?? '',
        quoteParentId ?? '',
        defaultValues ?? ImmutableMap()
      );
  }
};

const getCriticalIllnessPlanObject = (
  plan: RetreivedPlan | undefined,
  quoteRootId: string | undefined,
  quoteParentId: string | undefined,
  coverageEffDate: string,
  defaultValues: ImmutableMap<string, any> | undefined,
  situsStateCd: string
) => {
  switch (plan?.planCode) {
    case 'GCI5':
      return createNewGCI5PlanObjectWithDefault(
        plan,
        quoteRootId ?? '',
        quoteParentId ?? '',
        coverageEffDate,
        defaultValues ?? ImmutableMap(),
        situsStateCd
      );
    case 'GVCI4':
      return createNewGVCI4PlanObjectWithDefault(
        plan,
        quoteRootId ?? '',
        quoteParentId ?? '',
        coverageEffDate,
        defaultValues ?? ImmutableMap(),
        situsStateCd
      );
  }
};

const getAccidentPlanObject = (
  plan: RetreivedPlan | undefined,
  quoteRootId: string | undefined,
  quoteParentId: string | undefined,
  coverageEffDate: string,
  defaultValues: ImmutableMap<string, any> | undefined,
  situsStateCd: string | undefined
) => {
  switch (plan?.planCode) {
    case 'GVA6':
      return createNewGVA6PlanObjectWithDefault(
        plan,
        quoteRootId ?? '',
        quoteParentId ?? '',
        coverageEffDate,
        defaultValues ?? ImmutableMap(),
        situsStateCd ?? ''
      );
  }
};
